import React, {memo, useEffect} from "react";
import {FaqItemProps} from "@/types/faq";
import {useAppSelector} from "@/redux/hooks";
import {RootState} from "@/redux/store";
import {useDeleteFaqMutation} from "@/redux/apis";
import FaIcon from "@/components/utilities/fa-icon";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    CircularProgress,
    Collapse,
    IconButton,
    Typography
} from "@mui/material";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";
import AreYouSureDialog from "@/components/utilities/are-you-sure-dialog";
import {apiEndpoints} from "@/utils/api-endpoints";
import {mutate} from "swr";


function FaqItem({id, question, answer, expandedAccordion, handleChange, category, handleEdit}: FaqItemProps) {
    const isAdmin = useAppSelector((state: RootState) => state.user.currentUser?.roles?.includes("Admin"));
    const [deleteFaq, {isLoading}] = useDeleteFaqMutation();
    const theme = useTheme()
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [visualExpanded, setVisualExpanded] = React.useState(expandedAccordion);

    const handleConfirmDelete = async () => {
        try {
            await deleteFaq(id).unwrap();
            mutate(apiEndpoints.Faq.GetAll.url);

        } catch (err) {
            console.error(err);
        } finally {
            setDeleteDialogOpen(false);
        }
    };

    useEffect(() => {
        if (expandedAccordion) {
            setVisualExpanded(true);
        } else {
            const t = setTimeout(() => setVisualExpanded(false), 200);
            return () => clearTimeout(t);
        }
    }, [expandedAccordion]);


    return (
        <>
            <Box display = "flex" mb = {2} >
                <Accordion
                    key = {id}
                    expanded = {expandedAccordion}
                    onChange = {handleChange}
                    elevation = {0}
                    slots = {{transition: Collapse}}
                    slotProps = {{
                        transition: {
                            timeout: 300,
                            easing: theme.transitions.easing.easeInOut,
                        },
                    }}
                    sx = {{width: '100%'}}
                >
                    <AccordionSummary
                        expandIcon = {
                            <Box
                                sx = {{
                                    transition: theme.transitions.create(['color'], {
                                        duration: 200,
                                        easing: theme.transitions.easing.easeInOut,
                                    }),
                                }}

                            >
                                <FaIcon
                                    fontSize = {24}
                                    color = {visualExpanded ? theme.palette.error.main : theme.palette.primary.main}
                                    faIcon = {visualExpanded ? 'fa-minus-square' : 'fa-plus-square'}
                                    sx = {{cursor: 'pointer', mt: .5}}
                                />
                            </Box >
                        }

                        sx = {{
                            width: '100%',
                            color: visualExpanded ? theme.palette.primary.main : 'inherit',
                            px: 1.5,
                            minHeight: 56,
                            '&.Mui-expanded': {minHeight: 56},
                            '& .MuiAccordionSummary-content': {my: 0},
                            '& .MuiAccordionSummary-content.Mui-expanded': {my: 0},
                        }}
                    >
                        <Typography mr = {2} fontWeight = {400} fontSize = {16} >
                            {question}
                        </Typography >

                    </AccordionSummary >

                    <AccordionDetails sx = {{pb: 2.5}} >
                        <Typography fontSize = {16} >
                            {answer}
                        </Typography >
                    </AccordionDetails >

                </Accordion >
                {
                    isAdmin &&
                    <Stack gap = {1} direction = 'row' spacing = {1} ml = {2}>
                      <IconButton onClick = {() => handleEdit({id, question, answer, category})}
                          sx = {{
                              "&:hover": {bgcolor: "transparent"},
                          }}
                      >
                        <FaIcon faIcon = "fa-edit" fontSize = {20} color = {theme.palette.primary.main} />
                      </IconButton >
                      <IconButton
                          onClick = {() => setDeleteDialogOpen(true)}
                          sx = {{
                              "&:hover": {bgcolor: "transparent"},
                          }}
                      >
                          {
                              isLoading ? <CircularProgress size = {20} />
                                  :
                                  <FaIcon faIcon = "fa-trash" fontSize = {20} color = {theme.palette.error.main} />
                          }
                      </IconButton >
                    </Stack >
                }
            </Box >

            <AreYouSureDialog
                open = {deleteDialogOpen}
                title = "حذف سوال"
                message = "آیا از حذف این سوال متداول اطمینان دارید؟"
                onClose = {() => setDeleteDialogOpen(false)}
                onConfirm = {handleConfirmDelete}
            />

        </>
    );
}

export default memo(FaqItem);
